import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "./ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-destructive/10 via-destructive/5 to-warning/10 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="bg-background/80 backdrop-blur-sm rounded-lg shadow-lg border border-destructive/20 p-8">
              <div className="flex justify-center mb-6">
                <div className="h-24 w-24 bg-destructive/10 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-12 w-12 text-destructive" />
                </div>
              </div>

              <h1 className="text-2xl font-bold text-foreground mb-2">
                ¡Ups! Algo salió mal
              </h1>

              <p className="text-muted-foreground mb-6">
                Ha ocurrido un error inesperado. Por favor, intenta recargar la página.
              </p>

              {process.env.NODE_ENV === "development" && this.state.error && (
                <details className="mb-6 text-left">
                  <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                    Detalles del error (solo desarrollo)
                  </summary>
                  <div className="mt-2 p-3 bg-muted rounded text-xs font-mono overflow-auto">
                    <p className="text-destructive mb-2">{this.state.error.toString()}</p>
                    {this.state.errorInfo && (
                      <pre className="text-muted-foreground">{this.state.errorInfo.componentStack}</pre>
                    )}
                  </div>
                </details>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={this.handleRefresh}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Recargar página
                </Button>

                <Button
                  onClick={this.handleGoHome}
                  className="flex items-center gap-2"
                >
                  <Home className="h-4 w-4" />
                  Ir al inicio
                </Button>
              </div>
            </div>

            <p className="mt-6 text-sm text-muted-foreground">
              Si el problema persiste, por favor contacta al administrador.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}